import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import config from '../config/config'

// Marks every request (login, refresh and logout included) as the CMS's. The
// server keys the httpOnly refresh cookie on this header, so the CMS session
// lives in its own cookie and survives a portal login in the same browser.
axios.defaults.headers.common['x-cms-request'] = 'true';

export const AuthContext = createContext();

const AUTH_ROUTES = [
  '/api/user/login',
  '/api/user/refresh',
  '/api/user/logout',
  '/api/user/forgot-password',
  '/api/user/reset-password',
];

// Module-scoped so overlapping callers share one in-flight POST /refresh.
// The server rotates the cookie; a second concurrent refresh can 401.
let refreshPromise = null;

function decodeJwtPayload(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${config.apiUrl}/api/user/refresh`, {}, { withCredentials: true })
      .then(response => {
        const data = response.data;
        if (!data?.token) throw new Error('Refresh response did not include a token');
        axios.defaults.headers.common['x-auth-token'] = data.token;
        return data;
      })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [getRequestsCallback, setGetRequestsCallback] = useState(null);

  const applySession = (receivedToken, userInfo, orgs) => {
    axios.defaults.headers.common['x-auth-token'] = receivedToken;
    setToken(receivedToken);
    setUser(userInfo || decodeJwtPayload(receivedToken)?.user || {});
    setOrganizations(orgs || []);
  };

  const clearSession = () => {
    delete axios.defaults.headers.common['x-auth-token'];
    setToken(null);
    setUser(null);
    setOrganizations([]);
    localStorage.removeItem('organizations');
  };

  // Restore the session from the httpOnly refresh cookie on app load.
  useEffect(() => {
    Cookies.remove('authToken'); // clean up the legacy js-cookie from the old session model
    let active = true;
    refreshSession()
      .then(data => { if (active) applySession(data.token, data.user, data.organizations); })
      .catch(() => { if (active) clearSession(); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Silent refresh: on a 401, refresh once and retry the original request.
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      response => response,
      async (error) => {
        const original = error.config;
        const isAuthRoute = AUTH_ROUTES.some(route => original?.url?.includes(route));
        const hasSession = Boolean(axios.defaults.headers.common['x-auth-token']);
        if (error.response?.status !== 401 || !original || isAuthRoute || original._retried || !hasSession) {
          return Promise.reject(error);
        }
        original._retried = true;
        try {
          const data = await refreshSession();
          applySession(data.token, data.user, data.organizations);
          original.headers = original.headers || {};
          original.headers['x-auth-token'] = data.token;
          return axios(original);
        } catch {
          clearSession();
          return Promise.reject(error);
        }
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (getRequestsCallback && user && token) {
      getRequestsCallback();
    }
  }, [getRequestsCallback, user, token]);

  const login = async (email, password) => {
    const response = await axios.post(
      `${config.apiUrl}/api/user/login`,
      { email, password, login_type: 'vao', brand: 'clearcaid' },
      { withCredentials: true }
    );
    if (response.status === 202) {
      return {
        mfaRequired: true,
        challengeId: response.data.challenge_id,
        retryAfterSeconds: response.data.retry_after_seconds,
      };
    }

    const { token: receivedToken, user: userInfo, organizations: orgs } = response.data;
    applySession(receivedToken, userInfo, orgs);

    if (getRequestsCallback) {
      getRequestsCallback();
    }
    return { mfaRequired: false };
  };

  const verifyLoginMfa = async (challengeId, code) => {
    const response = await axios.post(
      `${config.apiUrl}/api/user/login/mfa/verify`,
      { challenge_id: challengeId, code },
      { withCredentials: true }
    );
    const { token: receivedToken, user: userInfo, organizations: orgs } = response.data;
    applySession(receivedToken, userInfo, orgs);
    return response.data;
  };

  const resendLoginMfa = async (challengeId) => {
    const response = await axios.post(
      `${config.apiUrl}/api/user/login/mfa/resend`,
      { challenge_id: challengeId }
    );
    return {
      challengeId: response.data.challenge_id,
      retryAfterSeconds: response.data.retry_after_seconds,
    };
  };

  const logout = async () => {
    clearSession();
    try {
      await axios.post(`${config.apiUrl}/api/user/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.log(err);
    }
  };

  const forgotPassword = async (email) => {
    await axios.post(`${config.apiUrl}/api/user/forgot-password`, { email });
  };

  const resetPassword = async (email, code, password) => {
    await axios.post(`${config.apiUrl}/api/user/reset-password`, { email, code, password });
  };

  const setGetRequests = (callback) => {
    setGetRequestsCallback(() => callback);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      organizations,
      token, 
      login, 
      verifyLoginMfa,
      resendLoginMfa,
      logout, 
      forgotPassword, 
      resetPassword, 
      loading,
      setGetRequests
    }}>
      {children}
    </AuthContext.Provider>
  );
}
